// front-end/src/components/case-manager/case-manager.js

import {apiGet, apiPatch, apiPost, apiPut} from '../../utils/api';
import {getAuthToken} from '../auth';
import {showErrorNotification, showSuccessNotification} from '../../utils/notifications';
import {openLinkPageModal} from '../diagnostic-manager/link-page-modal';
import {config} from '../../config/app-config';

export class CaseManager {
    constructor() {
        this.init();
    }

    init() {
        console.log('CaseManager initialized');
        document.addEventListener('blockPublished', this.handleBlockPublished.bind(this));
        document.addEventListener('blockAdded', this.handleBlockAdded.bind(this));
        this.processExistingCaseBlocks();
    }

    handleBlockAdded(event) {
        const { blockElement } = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');
        if (blockName && blockName.startsWith('case-card-') && getAuthToken()) {
            this.addLinkButtonToBlock(blockElement);
        }
    }

    // Проверяем, должны ли показываться статусы
    shouldShowStatusControls() {
        return window.location.pathname.match(/cases(\.html)?$/) && getAuthToken();
    }

    handleBlockPublished(event) {
        const {blockElement, blockId, blockData} = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');

        if (blockName && blockName.startsWith('case-card-')) {
            console.log('Case block published:', blockName);
            this.activateCaseLink(blockElement, blockName);

            const pendingId = blockElement.getAttribute('data-pending-link-id');
            if (pendingId) {
                this._applyPendingLink(blockElement, blockId, pendingId);
            } else {
                this.createCaseEntity(blockElement, blockId, blockData);
            }
        }
    }

    async _applyPendingLink(blockElement, blockId, entityId) {
        try {
            const result = await apiPatch(`${config.api.paths.casesLinkBlock}/${entityId}/block`, { block_id: parseInt(blockId, 10) });
            if (result?.id) {
                blockElement.setAttribute('data-news-id', result.id);
                blockElement.setAttribute('data-news-status', result.status || 'draft');
                blockElement.setAttribute('data-news-slug', result.slug);
                blockElement.removeAttribute('data-pending-link-id');
                blockElement.removeAttribute('data-pending-link-slug');
                this.activateCaseLink(blockElement, result.slug);
            }
        } catch (err) {
            console.error('Error applying pending case link:', err);
        }
    }

    activateCaseLink(blockElement, slug) {
        const caseLink = blockElement.querySelector('a.news-link-to-feed-container');
        if (caseLink) {
            const dynamicUrl = `/case/${slug}`;
            caseLink.href = dynamicUrl;
            caseLink.setAttribute('data-page', slug);
            caseLink.classList.remove('inactive');
            caseLink.classList.add('active');

            console.log(`Case link activated for ${slug}: ${dynamicUrl}`);
        }
    }

    async createCaseEntity(blockElement, blockId, blockData) {
        const token = getAuthToken();
        if (!token) return;

        try {
            const blockName = blockData.data_block_name;
            const caseSlug = blockName;

            const caseData = {
                title: `Case: ${blockName.replace('case-card-', '').replace(/-/g, ' ')}`,
                slug: caseSlug,
                preview_text: 'Case study preview',
                preview_image: null,
                page_title: `Case - ${blockName}`,
                page_slug: `case-${Date.now()}`,
                block_data_name: blockName
            };

            let result = await apiPost(config.api.paths.cases, caseData);
            if (!result?.id) {
                // Slug exists — find and link the existing entity
                const existing = await apiGet(`${config.api.paths.casesBySlug}/${caseSlug}`, { auth: false }).catch(() => null);
                if (existing?.id && blockId) {
                    result = await apiPatch(
                        `${config.api.paths.casesLinkBlock}/${existing.id}/block`,
                        { block_id: parseInt(blockId, 10) }
                    ).catch(() => null);
                }
            }

            if (result && result.id) {
                blockElement.setAttribute('data-news-id', result.id);
                blockElement.setAttribute('data-news-status', result.status || 'draft');
                blockElement.setAttribute('data-news-slug', result.slug);

                if (this.shouldShowStatusControls()) {
                    this.updateBlockStatusIndicator(blockElement, result.status || 'draft');
                }

                if (getAuthToken() && this.shouldShowStatusControls()) {
                    this.addStatusControlsToBlock(blockElement, result.id);
                }

                this.addLinkButtonToBlock(blockElement);
                showSuccessNotification('Кейс створено в статусі чернетки');
            }

        } catch (error) {
            console.error('Error creating case entity:', error);
            showErrorNotification('Помилка створення кейсу');
        }
    }

    addStatusControlsToBlock(blockElement, caseId) {
        if (!this.shouldShowStatusControls()) {
            return;
        }

        const existingControls = blockElement.querySelector('.news-status-controls');
        if (existingControls) {
            existingControls.remove();
        }

        const currentStatus = blockElement.getAttribute('data-news-status') || 'draft';

        const controlsContainer = document.createElement('div');
        controlsContainer.className = 'news-status-controls';
        controlsContainer.innerHTML = `
      <div class="status-controls-inner">
        <span class="current-status">Status: ${currentStatus.toUpperCase()}</span>
        <button class="toggle-status-btn" data-case-id="${caseId}" data-current-status="${currentStatus}">
          ${currentStatus === 'draft' ? 'Publish' : 'Unpublish'}
        </button>
      </div>
    `;

        blockElement.appendChild(controlsContainer);

        const toggleBtn = controlsContainer.querySelector('.toggle-status-btn');
        toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleCaseStatus(blockElement, caseId, currentStatus);
        });

        this._addSlugEditor(blockElement, caseId);

        console.log(`Status controls added to case block for ID: ${caseId}`);
    }

    async toggleCaseStatus(blockElement, caseId, currentStatus) {
        const token = getAuthToken();
        if (!token) {
            showErrorNotification('Authentication required');
            return;
        }

        try {
            const newStatus = currentStatus === 'draft' ? 'published' : 'draft';

            console.log(`Toggling case status from ${currentStatus} to ${newStatus}`);

            const result = await apiPost(`${config.api.paths.casesPublish}/${caseId}/publish`);

            if (result) {
                blockElement.setAttribute('data-news-status', result.status);
                this.updateBlockStatusIndicator(blockElement, result.status);
                this.updateStatusControls(blockElement, result.status);

                const statusText = result.status === 'published' ? 'published' : 'unpublished';
                showSuccessNotification(`Case ${statusText} successfully`);

                console.log('Case status updated successfully:', result);
            }

        } catch (error) {
            console.error('Error toggling case status:', error);
            showErrorNotification('Failed to update case status');
        }
    }

    updateBlockStatusIndicator(blockElement, status) {
        if (!this.shouldShowStatusControls()) {
            return;
        }

        let indicator = blockElement.querySelector('.news-status-indicator');

        if (!indicator) {
            indicator = document.createElement('div');
            indicator.className = 'news-status-indicator';
            blockElement.appendChild(indicator);
        }

        indicator.className = `news-status-indicator status-${status}`;
        indicator.innerHTML = `<span class="status-text">${status.toUpperCase()}</span>`;
    }

    updateStatusControls(blockElement, status) {
        const controls = blockElement.querySelector('.news-status-controls');
        if (controls) {
            const statusSpan = controls.querySelector('.current-status');
            const toggleBtn = controls.querySelector('.toggle-status-btn');

            if (statusSpan) {
                statusSpan.textContent = `Status: ${status.toUpperCase()}`;
            }

            if (toggleBtn) {
                toggleBtn.textContent = status === 'draft' ? 'Publish' : 'Unpublish';
                toggleBtn.setAttribute('data-current-status', status);
            }
        }
    }

    addLinkButtonToBlock(blockElement) {
        if (!getAuthToken()) return;
        if (blockElement.querySelector('.case-link-btn')) return;

        const btn = document.createElement('button');
        btn.className = 'case-link-btn block-control-btn';
        btn.title = 'Прив\'язати сторінку';
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/>
            <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z"/>
        </svg>`;
        Object.assign(btn.style, {
            position: 'absolute', top: '10px', right: '70px',
            background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '4px',
            color: 'white', cursor: 'pointer', padding: '4px', zIndex: '10',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        });

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const blockId = blockElement.getAttribute('data-block-id') || null;
            const currentSlug = blockElement.getAttribute('data-news-slug') || blockElement.getAttribute('data-pending-link-slug') || null;
            openLinkPageModal(blockId, currentSlug, (result) => {
                if (blockId) {
                    if (result.slug) {
                        blockElement.setAttribute('data-news-id', result.id);
                        blockElement.setAttribute('data-news-slug', result.slug);
                        const blockName = blockElement.getAttribute('data-block-name');
                        this.activateCaseLink(blockElement, blockName);
                    } else {
                        blockElement.removeAttribute('data-news-slug');
                    }
                } else {
                    blockElement.setAttribute('data-pending-link-id', result.id);
                    blockElement.setAttribute('data-pending-link-slug', result.slug);
                    btn.style.background = 'rgba(25,118,210,0.8)';
                }
            }, {
                listPath: `${config.api.paths.cases}?limit=200`,
                linkPath: config.api.paths.casesLinkBlock,
            });
        });

        blockElement.appendChild(btn);
    }

    processExistingCaseBlocks() {
        const caseBlocks = document.querySelectorAll('[data-block-name^="case-card-"][data-block-id]');

        caseBlocks.forEach(blockElement => {
            const blockName = blockElement.getAttribute('data-block-name');
            const newsId = blockElement.getAttribute('data-news-id');
            const newsStatus = blockElement.getAttribute('data-news-status') || 'draft';
            const newsSlug = blockElement.getAttribute('data-news-slug');

            // Активируем ссылку для опубликованных кейсов
            if (newsStatus === 'published' && newsSlug) {
                this.activateCaseLink(blockElement, blockName);
            }

            // Add status indicator - ТОЛЬКО на cases.html
            if (this.shouldShowStatusControls()) {
                this.updateBlockStatusIndicator(blockElement, newsStatus);
            }

            // Add status controls if case entity exists and user is authenticated
            if (newsId && getAuthToken() && this.shouldShowStatusControls()) {
                this.addStatusControlsToBlock(blockElement, newsId);
            }

            this.addLinkButtonToBlock(blockElement);
        });

        if (this.shouldShowStatusControls()) {
            this._addFixTitlesButton();
        }
    }

    _toSlug(text) {
        const uk = {
            'а':'a','б':'b','в':'v','г':'h','ґ':'g','д':'d','е':'e','є':'ye',
            'ж':'zh','з':'z','и':'y','і':'i','ї':'yi','й':'y','к':'k','л':'l',
            'м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
            'ф':'f','х':'kh','ц':'ts','ч':'ch','ш':'sh','щ':'shch','ь':'',
            'ю':'yu','я':'ya',
        };
        return text.toLowerCase()
            .split('').map(c => uk[c] ?? c).join('')
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_]+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    }

    _addSlugEditor(blockElement, caseId) {
        if (blockElement.querySelector('.slug-editor')) return;

        const currentSlug = blockElement.getAttribute('data-news-slug') || '';
        const apiPath = `${config.api.paths.cases}/${caseId}`;

        const wrap = document.createElement('div');
        wrap.className = 'slug-editor';
        Object.assign(wrap.style, {
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '4px 6px', background: 'rgba(0,0,0,0.55)',
            borderRadius: '0 0 4px 4px', flexWrap: 'wrap',
        });
        wrap.innerHTML = `
            <span style="color:#aaa;font-size:11px;white-space:nowrap">slug:</span>
            <input class="slug-input" type="text" value="${currentSlug}"
                style="flex:1;min-width:80px;font-size:11px;padding:2px 4px;border-radius:3px;border:1px solid #555;background:#222;color:#fff;outline:none">
            <button class="slug-auto-btn" title="Авто з заголовку"
                style="font-size:10px;padding:2px 5px;background:#444;border:none;border-radius:3px;color:#ccc;cursor:pointer;white-space:nowrap">Auto</button>
            <button class="slug-save-btn"
                style="font-size:10px;padding:2px 6px;background:#1976d2;border:none;border-radius:3px;color:#fff;cursor:pointer;white-space:nowrap">Save</button>
        `;

        const input = wrap.querySelector('.slug-input');
        const autoBtn = wrap.querySelector('.slug-auto-btn');
        const saveBtn = wrap.querySelector('.slug-save-btn');

        autoBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const titleEl = blockElement.querySelector('h2, h3, h4, .case-title, .news-feed-title');
            if (titleEl) input.value = this._toSlug(titleEl.textContent.trim());
        });

        saveBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const newSlug = input.value.trim();
            if (!newSlug || newSlug === currentSlug) return;
            try {
                saveBtn.textContent = '...';
                const result = await apiPut(apiPath, { slug: newSlug });
                if (result?.slug) {
                    blockElement.setAttribute('data-news-slug', result.slug);
                    input.value = result.slug;
                    const link = blockElement.querySelector('a.news-link-to-feed-container, a.active');
                    if (link) link.href = `/case/${result.slug}`;
                    saveBtn.textContent = '✓';
                    setTimeout(() => { saveBtn.textContent = 'Save'; }, 1500);
                    showSuccessNotification(`Slug оновлено: ${result.slug}`);
                }
            } catch (err) {
                saveBtn.textContent = 'Save';
                showErrorNotification('Помилка збереження slug');
            }
        });

        wrap.addEventListener('click', e => e.stopPropagation());
        input.addEventListener('keydown', e => e.stopPropagation());

        const controls = blockElement.querySelector('.news-status-controls');
        if (controls) controls.after(wrap);
        else blockElement.appendChild(wrap);
    }

    _addFixTitlesButton() {
        if (document.getElementById('case-fix-titles-btn')) return;

        const btn = document.createElement('button');
        btn.id = 'case-fix-titles-btn';
        btn.textContent = 'Fix Page Titles';
        Object.assign(btn.style, {
            position: 'fixed', bottom: '80px', right: '20px',
            background: '#c0392b', color: '#fff', border: 'none',
            borderRadius: '6px', padding: '8px 14px', fontSize: '12px',
            cursor: 'pointer', zIndex: '9999', boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        });

        btn.addEventListener('click', async () => {
            if (!confirm('Синхронізувати Page.title для всіх кейсів? Це виправить пропащі блоки на сторінках кейсів.')) return;
            try {
                btn.textContent = '...';
                const result = await apiPost(config.api.paths.caseFixPageTitles);
                btn.textContent = `✓ Fixed ${result.fixed?.length ?? 0}`;
                showSuccessNotification(`Виправлено: ${result.fixed?.length ?? 0}, вже ок: ${result.already_ok_count ?? 0}`);
                setTimeout(() => { btn.textContent = 'Fix Page Titles'; }, 4000);
            } catch (err) {
                btn.textContent = 'Fix Page Titles';
                showErrorNotification('Помилка виправлення page titles');
            }
        });

        document.body.appendChild(btn);
    }

    // Метод для загрузки атрибутов кейса после создания блока
    async loadCaseAttributes(blockElement, blockId, blockName) {
        try {
            const newsData = await apiGet(`${config.api.paths.casesByBlockId}/${blockId}`, {auth: false});

            if (newsData) {
                console.log(`Case data found for block ${blockName}:`, newsData);

                blockElement.setAttribute('data-news-id', newsData.id);
                blockElement.setAttribute('data-news-status', newsData.status || 'draft');
                blockElement.setAttribute('data-news-slug', newsData.slug);

                // Обновляем ссылку если кейс опубликован
                if (newsData.status === 'published' || blockId) {
                    const caseLink = blockElement.querySelector('a.news-link-to-feed-container');
                    if (caseLink) {
                        const dynamicUrl = newsData.slug && newsData.slug.startsWith('case-') ? `/case/${newsData.slug}` : `/news/${newsData.slug}`;
                        caseLink.href = dynamicUrl;
                        caseLink.setAttribute('data-page', newsData.slug);
                        caseLink.classList.remove('inactive');
                        caseLink.classList.add('active');

                        console.log(`Case link activated for ${blockName}: ${dynamicUrl}`);
                    }
                }

                // Обновляем индикатор статуса
                if (this.shouldShowStatusControls()) {
                    this.updateBlockStatusIndicator(blockElement, newsData.status || 'draft');
                }

                // Добавляем контролы статуса
                if (getAuthToken() && this.shouldShowStatusControls()) {
                    this.addStatusControlsToBlock(blockElement, newsData.id);
                }
            }
        } catch (error) {
            console.warn(`Failed to load case attributes for block ${blockName}:`, error);
        }
    }
}

export const caseManager = new CaseManager();

// Делаем caseManager доступным глобально
window.caseManager = caseManager;

console.log('CaseManager initialized and made globally available');